import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import { api } from '../Api';
import '../assets/ops.css';

// Every fine-grained staff capability, in display order. Keep the keys in sync
// with PERMISSION_KEYS in backend/utils.js — the server is the source of truth
// and re-checks each one; this list only decides what the editor shows.
export const PERMISSION_LIST = [
  {
    key: 'canAddProduct',
    label: 'Add & edit products',
    hint: 'Create products and change their details. Stock is never edited here.',
    icon: 'fa-gift',
  },
  {
    key: 'canManageStock',
    label: 'Manage stock',
    hint: 'Add units to a product (which mints their serials) and void damaged stock.',
    icon: 'fa-cubes',
  },
  {
    key: 'canPrintSticker',
    label: 'Print stickers',
    hint: 'Generate and re-print the 2-inch sticker sheets.',
    icon: 'fa-print',
  },
  {
    key: 'canDeliver',
    label: 'Run the delivery counter',
    hint: 'Verify customers and hand over stock. This is the one that moves inventory.',
    icon: 'fa-truck',
  },
  {
    key: 'canManageOrders',
    label: 'Manage orders',
    hint: 'View orders, update status, mark delivered. Cannot approve payment — that stays admin-only.',
    icon: 'fa-clipboard',
  },
  {
    key: 'canViewReports',
    label: 'View product reports',
    hint: 'See the reports dashboard: sales, stock and order reports. Read-only on its own.',
    icon: 'fa-bar-chart',
  },
  {
    key: 'canApproveTopups',
    label: 'Verify topups (Accounts)',
    hint: 'Accounts: sees new topup requests, confirms the payment arrived or rejects it. Admins only see a topup after this. Releasing the balance stays admin-only.',
    icon: 'fa-money',
  },
  {
    key: 'canManageWithdraw',
    label: 'Manage withdrawals',
    hint: 'Handle member withdrawal requests from the withdraw queue.',
    icon: 'fa-dollar',
  },
  {
    key: 'canEnrollFingerprint',
    label: 'Enrol fingerprints',
    hint: 'Capture and replace a customer’s stored fingerprint templates.',
    icon: 'fa-hand-paper-o',
  },
  {
    key: 'canManageStaff',
    label: 'Manage staff roles',
    hint: 'Open this same editor for other users — assign Agent / Product Manager roles and capabilities. Never grants Admin.',
    icon: 'fa-users',
  },
];

// The staff ROLE flags a manager may assign, in display order. Admin is
// deliberately absent — it can never be granted from this editor.
export const ROLE_TOGGLES = [
  {
    key: 'isAgent',
    label: 'Agent',
    hint: 'Staff member. Lands in the agent portal at login and sees the menu items their capabilities allow.',
    icon: 'fa-user',
    accent: '#0d6efd',
    border: '#cfe2ff',
    bg: '#f2f8ff',
  },
  {
    key: 'isUserManager',
    label: 'User Manager',
    hint: 'Full internal-user administration: block, reset passwords, and assign roles to others. Cannot grant Admin.',
    icon: 'fa-user-plus',
    accent: '#7c3aed',
    border: '#e2d9f3',
    bg: '#f6f2ff',
  },
  {
    key: 'isProductManager',
    label: 'Product Manager',
    hint: 'Own dashboard and reports. Pair with the capabilities below to let them manage products and orders.',
    icon: 'fa-briefcase',
    accent: '#0d6efd',
    border: '#cfe2ff',
    bg: '#f2f8ff',
  },
];

// The staff role + capability editor, shared by the user list and the agent
// list. Reads the target's current roles/permissions and the actor's rights
// from GET /:id/permissions, and writes them back. Every rule shown here is
// re-enforced server-side on the PUT.
export default function StaffPermissionsModal({ user, token, onClose, onSaved }) {
  const [permissions, setPermissions] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [roles, setRoles] = useState({
    isAgent: false,
    isUserManager: false,
    isProductManager: false,
  });
  const [editable, setEditable] = useState({ canEdit: true, actorIsAdmin: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    axios
      .get(`${api}/api/users/${user._id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (!live) return;
        setPermissions(data.permissions);
        setIsAdminUser(data.isAdmin);
        setRoles({
          isAgent: Boolean(data.isAgent),
          isUserManager: Boolean(data.isUserManager),
          isProductManager: Boolean(data.isProductManager),
        });
        if (data.editable) setEditable(data.editable);
      })
      .catch((err) => toast.error(getError(err)))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [user._id, token]);

  const readOnly = !editable.canEdit;
  const toggleRole = (key) => { if (!readOnly) setRoles((r) => ({ ...r, [key]: !r[key] })); };
  const togglePerm = (key) => { if (!readOnly) setPermissions((c) => ({ ...c, [key]: !c[key] })); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${api}/api/users/${user._id}/permissions`,
        { permissions, ...roles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data.message);
      if (onSaved) onSaved(data);
      onClose();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const granted = permissions ? Object.values(permissions).filter(Boolean).length : 0;
  const sectionLabel = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'var(--ops-muted)',
    margin: '4px 0 8px',
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4><i className="fa fa-shield" style={{ marginRight: 8, color: '#0ea5e9' }} />Staff roles &amp; permissions</h4>
            <p className="sub">{user.name} · @{user.username}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          {loading ? (
            <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
          ) : isAdminUser ? (
            <div className="ops-note info">
              <i className="fa fa-info-circle" />
              <span>
                <strong>{user.username}</strong> is an admin and already holds every capability.
                Roles and permissions don’t apply, and an admin account can’t be edited here.
              </span>
            </div>
          ) : (
            <>
              {readOnly && (
                <div className="ops-note info" style={{ marginTop: 0 }}>
                  <i className="fa fa-lock" />
                  <span>You can view this account but can’t change your own roles or permissions. Ask an admin.</span>
                </div>
              )}
              <p style={{ fontSize: 13, color: 'var(--ops-muted)', marginTop: 0 }}>
                {granted === 0
                  ? 'This is a regular customer account with no staff access.'
                  : `${granted} capability${granted === 1 ? '' : 'ies'} granted.`}
              </p>

              {/* ---- Staff ROLES. Each decides the login shell a user lands in.
                  Admin is never here — it can't be granted from this editor. */}
              <div style={sectionLabel}>Roles</div>
              <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                {ROLE_TOGGLES.map((role) => (
                  <div
                    key={role.key}
                    className="ops-switch"
                    style={{
                      border: `1px solid ${role.border}`,
                      background: role.bg,
                      borderRadius: 8,
                      opacity: readOnly ? 0.6 : 1,
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => toggleRole(role.key)}
                  >
                    <div className="txt">
                      <strong>
                        <i className={`fa ${role.icon}`} style={{ marginRight: 7, color: role.accent }} />
                        {role.label}
                      </strong>
                      <span>{role.hint}</span>
                    </div>
                    <button type="button" className={`ops-knob ${roles[role.key] ? 'on' : ''}`} aria-label={`${role.label} role`} />
                  </div>
                ))}
              </div>

              {/* ---- Fine-grained CAPABILITIES ---- */}
              <div style={sectionLabel}>Capabilities</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {PERMISSION_LIST.map((item) => (
                  <div
                    key={item.key}
                    className="ops-switch"
                    style={{ opacity: readOnly ? 0.6 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    onClick={() => togglePerm(item.key)}
                  >
                    <div className="txt">
                      <strong>
                        <i className={`fa ${item.icon}`} style={{ marginRight: 7, color: '#64748b' }} />
                        {item.label}
                      </strong>
                      <span>{item.hint}</span>
                    </div>
                    <button
                      type="button"
                      className={`ops-knob ${permissions[item.key] ? 'on' : ''}`}
                      aria-label={item.label}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="ops-modal-foot">
          <button className="ops-btn" onClick={onClose}>Close</button>
          {!isAdminUser && !loading && !readOnly && (
            <button className="ops-btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
